import { Medal } from './Achievements.tsx';
import { useState } from 'react';
import { api } from '../api.ts';
import { useGame, money, fmt, ago, CLASS_INFO } from '../state.tsx';
import { Loading, useLoad, PlayerLink, Panel, Medals, go, ItemIcon, ItemName, PrestigeStar, ROMAN } from '../components/ui.tsx';
import { Portrait } from '../art/Portrait.tsx';
import { StatIcon } from '../art/Scenes.tsx';
import { useAttack } from './Fight.tsx';
import { GiveButtons } from '../components/v08.tsx';
import { StatueIcon } from '../art/NycMap.tsx';

const GRUDGE: Record<string, string> = { robbed: 'robbing them', beaten: 'beating them in a fight', hit: 'collecting a bounty on them', turf: 'taking their turf' };

export function ProfilePage({ id }: { id: number }) {
  const { me, byId, act, content } = useGame();
  const attack = useAttack();
  const { data, reload } = useLoad(() => api.get(`/api/players/${id}`), [id, me?.level, me?.skillPoints]);
  const [hitOpen, setHitOpen] = useState(false);
  const [hitAmount, setHitAmount] = useState('');
  if (!data || !me) return <Loading />;
  const self = data.isSelf;
  const r = content.rules;
  const skills: [string, string, string, number][] = [
    ['attack', 'Attack', `${me.attack}`, 1],
    ['defense', 'Defense', `${me.defense}`, 1],
    ['health', 'Max Health', `${me.maxHealth} (+${r.healthPerSkillPoint} per point)`, 1],
    ['mafia', 'Mafia Limit', `${me.mafiaMembers}/${me.mafiaCap} members`, r.mafiaSlotCost],
  ];
  const accent = ACCENT_COLORS[data.accent] ?? ACCENT_COLORS.gold;
  return (
    <div>
      <div className="row profile-head" style={{ borderColor: accent }}>
        <div>
          <div style={{ border: `3px solid ${accent}`, display: 'inline-block' }}><Portrait seed={data.portrait} playerId={data.id} size={120} /></div>
        </div>
        <div className="grow">
          <h1 className="page-title" style={{ marginBottom: 2, color: accent }}>{data.name}{data.highRoller && <span className="high-roller-crown" title="High Roller: last week’s biggest casino winner"> ♛</span>}</h1>
          {data.title && <div className="profile-title" style={{ color: accent }}>“{data.title}”</div>}
          <div>Level <b>{data.level}</b>{data.prestige > 0 && <> <a className="profile-prestige" onClick={() => go('prestige')}><PrestigeStar rank={data.prestige} size={18} /> Prestige {ROMAN[data.prestige]}</a></>} <span className="gold">{CLASS_INFO[data.charClass]?.name}</span> · Mafia of <b>{data.mafiaSize}</b> · joined {ago(data.joinedAt)}</div>
          {data.motto && <i className="muted">“{data.motto}”</i>}
          {data.about && <div className="about-me">{data.about}</div>}
          {data.pinned.length > 0 && (
            <div className="pinned-badges">
              {data.pinned.map((a: any) => (
                <span key={a.id} className="pinned-badge" title={a.description}><Medal tier={a.hidden ? 'secret' : a.tier} size={22} /> {a.name}</span>
              ))}
            </div>
          )}
          {data.highRoller && <div className="small"><span className="locked-stamp" style={{ color: 'var(--gold)', borderColor: 'var(--gold)' }}>High Roller</span> of the week</div>}
          {data.statues?.length > 0 && <div className="statue-line"><StatueIcon size={18} /> Has a statue in the city ({data.statues.join(', ')})</div>}
          {data.fightStreak >= 3 && <div className="streak-line-p">On a <b>{data.fightStreak}-fight</b> winning streak</div>}
          {data.boroughs?.length > 0 && <div className="gold" style={{ marginTop: 4 }}>Controls {data.boroughs.map((b: string) => content.boroughs.find((x: any) => x.id === b)?.name ?? b).join(', ')}</div>}
          {data.rivalry && data.rivalry.fights > 0 && (
            <div className="head-to-head">
              {data.rivalry.rivals && <span className="rival-tag">Rival</span>}
              Head to head: you <b className="good">{data.rivalry.wins}</b> – <b className="bad">{data.rivalry.losses}</b> {data.name}
              {data.rivalry.rivals ? <span className="small"> · beating them pays {r.rivalry.bonusPct}% extra</span> : <span className="small dim"> · {r.rivalry.fights} fights in {r.rivalry.days} days makes you rivals</span>}
            </div>
          )}
          {data.grudge && <div className="bad" style={{ marginTop: 4 }}>Holding a grudge against <PlayerLink id={data.grudge.id} name={data.grudge.name} /> for {GRUDGE[data.grudge.kind] ?? 'crossing them'}.</div>}
          {data.bounty > 0 && <div className="bad" style={{ marginTop: 4 }}>☠ Wanted: <b>{money(data.bounty)}</b> bounty on the <a href="#/fight/hitlist">Hitlist</a></div>}
          {self && data.isHuman && <div style={{ marginTop: 6 }}><a className="btn small gray" href="#/customize">✎ Customize profile</a></div>}
          {!self && (
            <div style={{ marginTop: 8 }}>
              <button className={`btn ${data.inMyMafia || !data.healthy ? 'disabled' : ''}`} onClick={async () => { await attack(data.id, data.name, data.portrait); reload(); }}>Attack</button>{' '}
              {data.inMyMafia ? <span className="good">✓ In your mafia</span> : data.invited ? <span className="muted">Invitation sent</span> : (
                <button className="btn blue" onClick={async () => { await act('/api/mafia/invite', { playerId: data.id }, { success: (x) => ({ kind: 'good', text: x.joined ? `${data.name} joined your mafia!` : `Invitation sent to ${data.name}.` }) }); reload(); }}>Invite to Mafia</button>
              )}
              {' '}<button className="btn gray" onClick={() => setHitOpen(!hitOpen)}>Put a Hit Out</button>
              {data.isHuman && <>{' '}<a className="btn gray" href={`#/sidebets/new-${data.id}`}>Side Bet</a></>}
              {data.isHuman && me.isHuman && <div style={{ marginTop: 6 }}><GiveButtons to={{ id: data.id, name: data.name }} /></div>}
              {!data.healthy && <span className="small muted"> {data.name} is too weak to fight right now.</span>}
              {hitOpen && (
                <form className="hit-form" onSubmit={async (e) => {
                  e.preventDefault();
                  const amount = Math.floor(Number(hitAmount.replace(/[^0-9]/g, '')));
                  const res = await act('/api/hitlist/place', { targetId: data.id, amount }, { success: (x) => ({ kind: 'good', text: <>You put a <span className="money">{money(x.amount)}</span> bounty on {data.name} (fee {money(x.fee)}).</> }) });
                  if (res) { setHitOpen(false); reload(); }
                }}>
                  Bounty $ <input value={hitAmount} placeholder={String(data.minBounty)} onChange={(e) => setHitAmount(e.target.value)} style={{ width: 110 }} />{' '}
                  <button className="btn small">Place bounty</button>
                  <div className="small muted">Minimum {money(data.minBounty)}, plus a {r.hitlistFeePct}% fee, paid from cash on hand. Whoever takes {data.name} out collects it.</div>
                </form>
              )}
            </div>
          )}
        </div>
        {data.showcaseItem && (
          <div className="showcase">
            <ItemIcon itemId={data.showcaseItem} size={64} />
            <div className="small"><ItemName itemId={data.showcaseItem} /></div>
            <div className="small dim">prized possession</div>
          </div>
        )}
      </div>

      {self && (
        <Panel title={<>Skill Points: <span className="gold">{me.skillPoints}</span> available</>}>
          <table className="list stack">
            <tbody>
              {skills.map(([stat, label, val, cost]) => (
                <tr key={stat}>
                  <td style={{ width: 140 }}><b>{label}</b></td>
                  <td>{val}</td>
                  <td className="small muted">{stat === 'mafia' ? `+1 member slot for ${cost} points` : cost > 1 ? `${cost} points each` : ''}</td>
                  <td className="right">
                    {[1, 5].map((n) => (
                      <button key={n} className={`btn small ${me.skillPoints >= n * cost ? '' : 'disabled'}`} style={{ marginLeft: 4 }}
                        onClick={() => act('/api/skills/allocate', { stat, points: n })}>+{n}</button>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}

      {data.isHuman && <Wall profileId={data.id} ownerName={data.name} isSelf={self} posts={data.wall} reload={reload} />}

      <div className="row">
        <div className="grow">
          <Panel title="Record">
            <div className="stat-grid">
              {data.isHuman && <div><span>Login streak</span><b>{fmt(data.loginStreak)} <span className="small dim">(best {fmt(data.bestStreak)})</span></b></div>}
              {data.isHuman && <div><span>Missions done</span><b>{fmt(data.missionsDone)}</b></div>}
              <div><span>Best winning streak</span><b>{fmt(data.bestFightStreak)}</b></div>
              <div><span>Jobs completed</span><b>{fmt(data.jobsDone)}</b></div>
              <div><span>Properties</span><b>{fmt(data.properties)}</b></div>
              <div><span>Fights won</span><b>{fmt(data.fightsWon)}</b></div>
              <div><span>Fights lost</span><b>{fmt(data.fightsLost)}</b></div>
              <div><span>Defended</span><b>{fmt(data.defendsWon)}</b></div>
              <div><span>Beaten when attacked</span><b>{fmt(data.defendsLost)}</b></div>
              <div><span>Robberies pulled off</span><b>{fmt(data.robsWon)}</b></div>
              <div><span>Bounties collected</span><b>{fmt(data.hitsCollected)}</b></div>
              <div><span>Bosses beaten</span><b>{fmt(data.bossesBeaten)}</b></div>
              <div><span>Heists pulled off</span><b>{fmt(data.heistsDone)}</b></div>
              <div><span>Marketplace sales</span><b>{fmt(data.marketSales)}</b></div>
              {data.highRollerWeeks > 0 && <div><span>Weeks as High Roller</span><b>{fmt(data.highRollerWeeks)}</b></div>}
              {self && <div><span><StatIcon kind="attack" /> Attack skill</span><b>{data.attack}</b></div>}
              {self && <div><span><StatIcon kind="defense" /> Defense skill</span><b>{data.defense}</b></div>}
            </div>
          </Panel>
          <Panel title="Recent Fights">
            {data.recentFights.length === 0 ? <div className="muted">No fights yet.</div> : data.recentFights.map((f: any) => (
              <div key={f.id} className="feed-item">
                <div className="grow">
                  <PlayerLink id={f.attackerId} name={f.attackerName} /> attacked <PlayerLink id={f.defenderId} name={f.defenderName} /> and{' '}
                  {f.winnerId === f.attackerId ? <span className="good">won</span> : <span className="bad">lost</span>} ({money(f.cash)})
                </div>
                <span className="when">{ago(f.createdAt)}</span>
              </div>
            ))}
          </Panel>
        </div>
        <div style={{ width: 270 }}>
          <Panel title="Job Mastery">
            {data.masteries.length === 0 ? <div className="muted">None yet.</div> : data.masteries.map((m: any) => (
              <div key={m.tier_id} className="feed-item"><span className="grow">{byId.tiers.get(m.tier_id)?.name}</span><Medals level={m.mastery_level} /></div>
            ))}
          </Panel>
          <Panel title={`Achievements (${data.achievements.length}/${content.achievements.length})`} right={self ? <a className="small" onClick={() => go('achievements')}>all & progress</a> : undefined}>
            {data.achievements.length === 0 ? <div className="muted">None yet.</div> : [...data.achievements].reverse().slice(0, 12).map((a: any) => (
              <div key={a.achievement_id} className="feed-item" title={a.description}>
                <Medal tier={a.hidden ? 'secret' : a.tier} size={18} />
                <span className="grow">{a.name}<div className="small dim">{a.description}</div></span>
              </div>
            ))}
            {data.achievements.length > 12 && <div className="small dim">…and {data.achievements.length - 12} more</div>}
          </Panel>
          {data.earnedTitles?.length > 0 && (
            <Panel title={`Titles (${data.earnedTitles.length})`} right={self ? <a className="small" href="#/achievements/titles">all titles</a> : undefined}>
              <div className="title-chips">{data.earnedTitles.map((t: any) => <span key={t.name} className="title-chip" title={t.how}>{t.name}</span>)}</div>
            </Panel>
          )}
          {data.uniqueItems?.length > 0 && (
            <Panel title="One of a kind">
              {data.uniqueItems.map((id: string) => <div key={id} className="feed-item" style={{ alignItems: 'center' }}><ItemIcon itemId={id} size={32} /><span className="grow"><ItemName itemId={id} /></span></div>)}
            </Panel>
          )}
          {data.rivals.length > 0 && (
            <Panel title="Rivals">
              {data.rivals.map((rv: any) => (
                <div key={rv.id} className="feed-item" style={{ alignItems: 'center' }}>
                  <Portrait seed={rv.portrait} playerId={rv.id} size={26} />
                  <span className="grow"><PlayerLink id={rv.id} name={rv.name} /> <span className="dim small">L{rv.level}</span></span>
                  <span className="small" title="Wins – losses, all time">{rv.wins}–{rv.losses}</span>
                </div>
              ))}
            </Panel>
          )}
          {data.mafiaPreview.length > 0 && (
            <Panel title="Mafia" right={self ? <a className="small" onClick={() => go('mafia')}>manage</a> : undefined}>
              <div className="row" style={{ flexWrap: 'wrap', gap: 4 }}>
                {data.mafiaPreview.map((m: any) => <a key={m.id} title={`${m.name} (L${m.level})`} onClick={() => go(`profile/${m.id}`)}><Portrait seed={m.portrait} playerId={m.id} size={34} /></a>)}
              </div>
            </Panel>
          )}
        </div>
      </div>
    </div>
  );
}

export const ACCENT_COLORS: Record<string, string> = {
  gold: '#ffcc33', red: '#ff5544', blue: '#5aa8ff', green: '#77dd44', purple: '#cc88ff', silver: '#dddddd',
};

/** Profile wall: only real players have one, and only real players can post. */
function Wall({ profileId, ownerName, isSelf, posts, reload }: { profileId: number; ownerName: string; isSelf: boolean; posts: any[]; reload: () => void }) {
  const { act, me, user } = useGame();
  const [text, setText] = useState('');
  return (
    <Panel title={`${isSelf ? 'Your' : `${ownerName}’s`} Wall`}>
      <form onSubmit={async (e) => {
        e.preventDefault();
        const r = await act('/api/wall/post', { profileId, text });
        if (r) { setText(''); reload(); }
      }} className="row" style={{ marginBottom: 6 }}>
        <input className="grow" value={text} maxLength={300} onChange={(e) => setText(e.target.value)} placeholder={isSelf ? 'Write something on your wall…' : `Write something to ${ownerName}…`} />
        <button className="btn small">Post</button>
      </form>
      {posts.length === 0 ? <div className="muted">Nothing here yet.</div> : posts.map((p: any) => (
        <div key={p.id} className="feed-item wall-post">
          <Portrait seed={p.authorPortrait} playerId={p.authorId} size={28} />
          <div className="grow"><PlayerLink id={p.authorId} name={p.authorName} />: <span className="wall-text">{p.text}</span></div>
          <span className="when">
            <a className={`like-btn ${p.likedByMe ? 'liked' : ''}`} title={p.likers?.length ? `Liked by ${p.likers.join(', ')}` : 'Like'} onClick={async () => { await act('/api/wall/like', { postId: p.id }); reload(); }}>
              {p.likedByMe ? '♥' : '♡'} {p.likes > 0 ? p.likes : 'Like'}
            </a> · {ago(p.createdAt)}
            {(isSelf || p.authorId === me.id || user?.role === 'admin') && <> · <a onClick={async () => { await act('/api/wall/delete', { postId: p.id }); reload(); }}>delete</a></>}
          </span>
        </div>
      ))}
    </Panel>
  );
}
